import { useTranslation } from '@/composables/useTranslation'
import { WebSocketProgressReport } from '@/models/Communication'
import { AnyObject, Modify, StringObject } from '@/models/GenericInterfaces'
import { PimLocationQuery, Progress, ProgressType } from '@/models/Global'
import { ImportResult, ProductImportMappings, ProductInput, SkuRenameMapping } from '@/models/Import'
import Message from '@/models/Message'
import { SimplifiedProduct } from '@/models/Product'
import { SimpleTaxonomy } from '@/models/Taxonomy'
import { useDefaultStore } from '@/store'
import { useProductsStore } from '@/store/productsArchiveStore'
import ApiService from './ApiService'
import { useProductsBulkStore } from '@/store/productsBulkStore'
import { AxiosError } from 'axios'
import DataService from './DataService'

class ImportService {
  private get store() {
    return useDefaultStore()
  }

  private get productsStore() {
    return useProductsStore()
  }

  private get productsBulkStore() {
    return useProductsBulkStore()
  }

  private get appTranslate() {
    const { appTranslate } = useTranslation()
    return appTranslate
  }

  /**
   * Used when creating a new product by setting a starting point from an existing product
   * @throws error if product not found
   */
  async getProductStartingPoint(sku?: string): Promise<ProductInput> {
    if (sku) {
      return ApiService.get(`products/starting-points/${sku}`, undefined, 'v2')
    } else {
      return ApiService.get(`products/empty-starting-point`, undefined, 'v2')
    }
  }

  async getProductStartingPoints(query: PimLocationQuery): Promise<ProductInput[]> {
    const queryParams = DataService.getParams(query)
    return ApiService.get(`products/starting-points?${queryParams.toString()}`, undefined, 'v2')
  }

  // async getSanitizedProduct<T extends SimplifiedProduct | ProductPreview>(product: T): Promise<T> {
  //   return ApiService.post(`products/${product.sku}/sanitize`, product, undefined, 'v2')
  // }

  async getTemporarySkus(amount = 1) {
    return ApiService.get<string[]>(`products/sku/generate?amount=${amount}`, undefined, 'v2')
  }

  /**
   * Takes a parsed file and import mappings to create product previews
   */
  async mappingsToProductInputs(rows: StringObject[], mappings: ProductImportMappings, replaceExistingTaxonomies: Partial<Record<SimpleTaxonomy, boolean>>) {
    try {
      const result = await ApiService.post<Array<ProductInput & { sku: string }>>(
        `products/import/mappings-to-previews`,
        { rows, mappings, replaceExistingTaxonomies, mappingsUsesWrapper: true },
        undefined,
        'v2',
      )
      return result
    } catch (err: any) {
      this.store.addMessage(new Message(this.appTranslate('An error occured'), err.response?.data || err.message || err, 'danger'))
    }
  }

  /**
   * Import multiple products
   */
  async importProducts(
    productInputBySku: Record<string, ProductInput>,
    mappings: ProductImportMappings | undefined,
    replaceExistingTaxonomies: Partial<Record<SimpleTaxonomy | 'all', boolean>>,
    globalAppendMultiValuedAttributes: boolean,
    progressType = 'productsImport' as ProgressType,
  ) {
    const ws = await ApiService.ws(
      `products/import`,
      new URLSearchParams({ type: progressType }),
      {
        productInputBySku,
        mappings,
        replaceExistingTaxonomies,
        language: this.store.translationLanguage,
        globalAppendMultiValuedAttributes,
      },
      'v3',
    )

    const result = await new Promise((resolve: (_obj: ImportResult) => void, reject) => {
      ws.onmessage = (e) => {
        const report: WebSocketProgressReport<ImportResult> = JSON.parse(e.data)

        if (report.status === 'starting') {
          this.store.setProgressById({
            id: 'main',
            progressType,
            currentAction: report.msg,
            percentage: report.progressPercent,
          } as Progress)
        } else if (report.status === 'inprogress') {
          this.store.setProgressById({
            id: report.progressPercent !== undefined ? report.msg : 'main',
            progressType,
            currentAction: report.msg,
            percentage: report.progressPercent,
          } as Progress)

          this.store.setProgressById({
            id: 'main',
            progressType,
            currentAction: 'Importerar...',
          } as Progress)
        } else if (report.status === 'finished') {
          const msgs = [
            `${report.data?.imported.length} artiklar har importerats`,
            report.data?.skipped?.length ? `${report.data.skipped.length} artiklar skippades pga att de inte hade värde i någon angiven mappning` : '',
          ].filter((str) => !!str)

          this.store.setProgressById({
            id: 'main',
            progressType,
            currentAction: msgs.join('. '),
            percentage: report.progressPercent,
            finished: true,
          } as Progress)

          if (report.data) {
            resolve(report.data)
          } else {
            reject('Expected import result, but got none')
          }
        } else if (report.status === 'error') {
          reject(report.msg)
        }
      }

      ws.onclose = (e) => {
        reject(e.reason)
      }
    })

    this.productsBulkStore.latestImportedProducts = result

    return result
  }

  async getNewEditingSessionId() {
    return ApiService.get('editing-sessions/new')
  }
}
export default new ImportService()
